up.util = (function () {
    function noop() {
    }
    function asyncNoop() {
        return Promise.resolve();
    }
    function memoize(func, cacheProp) {
        let cache = {};
        return function (...args) {
            if (cacheProp)
                cache = (this[cacheProp] ||= {});
            if (cache.value)
                return cache.value[0];
            if (cache.error)
                throw cache.error;
            try {
                return (cache.value = [func.apply(this, args)])[0];
            }
            catch (error) {
                throw (cache.error = error);
            }
        };
    }
    function normalizeURL(url, options) {
        options = newOptions(options, { host: 'cross-domain' });
        const parts = parseURL(url);
        let normalized = '';
        if (options.host === 'cross-domain') {
            options.host = isCrossOrigin(parts);
        }
        if (options.host) {
            normalized += parts.protocol + "//" + parts.host;
        }
        let { pathname } = parts;
        if (options.trailingSlash === false && pathname !== '/') {
            pathname = pathname.replace(/\/$/, '');
        }
        normalized += pathname;
        if (options.search !== false) {
            normalized += parts.search;
        }
        if (options.hash !== false) {
            normalized += parts.hash;
        }
        return normalized;
    }
    function matchURLs(leftURL, rightURL, options) {
        return matchableURL(leftURL, options) === matchableURL(rightURL, options);
    }
    function matchableURL(url, options) {
        if (!url)
            return;
        return normalizeURL(url, { hash: false, trailingSlash: false, ...options });
    }
    function matchableURLPatternAtom(patternAtom) {
        if (patternAtom.endsWith('/'))
            return [patternAtom, patternAtom.slice(0, -1)];
        if (patternAtom.includes('/?'))
            return [patternAtom, patternAtom.replace('/?', '?')];
        if (patternAtom.endsWith('/*'))
            return [patternAtom, patternAtom.slice(0, -2), patternAtom.slice(0, -2) + '?*'];
        return patternAtom;
    }
    const APP_PROTOCOL = location.protocol;
    const APP_HOSTNAME = location.hostname;
    function isCrossOrigin(urlOrAnchor) {
        if (isString(urlOrAnchor) && (urlOrAnchor.indexOf('//') === -1)) {
            return false;
        }
        const parts = parseURL(urlOrAnchor);
        return (APP_HOSTNAME !== parts.hostname) || (APP_PROTOCOL !== parts.protocol);
    }
    function parseURL(url) {
        if (url.pathname) {
            return url;
        }
        let link = document.createElement('a');
        link.href = url;
        return link;
    }
    function normalizeMethod(method) {
        return method ? method.toUpperCase() : 'GET';
    }
    function methodAllowsPayload(method) {
        return (method !== 'GET') && (method !== 'HEAD');
    }
    function iteratee(block) {
        if (isString(block)) {
            return (item) => item[block];
        }
        else {
            return block;
        }
    }
    function map(list, block) {
        if (list.length === 0) {
            return [];
        }
        block = iteratee(block);
        let mapped = [];
        let i = 0;
        for (let item of list) {
            mapped.push(block(item, i++));
        }
        return mapped;
    }
    function mapObject(array, pairer) {
        return Object.fromEntries(array.map(pairer));
    }
    function each(array, block) {
        let i = 0;
        for (let item of array) {
            block(item, i++);
        }
    }
    function isNull(value) {
        return value === null;
    }
    function isTruthy(value) {
        return !!value;
    }
    function isUndefined(value) {
        return value === undefined;
    }
    const isDefined = negate(isUndefined);
    function isMissing(value) {
        return isUndefined(value) || isNull(value);
    }
    const isGiven = negate(isMissing);
    function isBlank(value) {
        if (isMissing(value)) {
            return true;
        }
        if (isObject(value) && value[isBlank.key]) {
            return value[isBlank.key]();
        }
        if (isString(value) || isList(value)) {
            return value.length === 0;
        }
        if (isOptions(value)) {
            return Object.keys(value).length === 0;
        }
        return false;
    }
    isBlank.key = 'up.util.isBlank';
    function presence(value, tester = isPresent) {
        if (tester(value)) {
            return value;
        }
    }
    const isPresent = negate(isBlank);
    function isFunction(value) {
        return typeof (value) === 'function';
    }
    function isString(value) {
        return (typeof (value) === 'string') || value instanceof String;
    }
    function isBoolean(value) {
        return (typeof (value) === 'boolean') || value instanceof Boolean;
    }
    function isNumber(value) {
        return (typeof (value) === 'number') || value instanceof Number;
    }
    function isOptions(value) {
        return (typeof (value) === 'object') && !isNull(value) && (isUndefined(value.constructor) || (value.constructor === Object));
    }
    function isObject(value) {
        const typeOfResult = typeof (value);
        return ((typeOfResult === 'object') && !isNull(value)) || (typeOfResult === 'function');
    }
    function isElement(value) {
        return value instanceof Element;
    }
    function isTextNode(value) {
        return value instanceof Text;
    }
    function isRegExp(value) {
        return value instanceof RegExp;
    }
    function isError(value) {
        return value instanceof Error;
    }
    function isJQuery(value) {
        return up.browser.canJQuery() && value instanceof jQuery;
    }
    function isElementLike(value) {
        return !!(value && (value.addEventListener || (isJQuery(value) && value[0]?.addEventListener)));
    }
    function isPromise(value) {
        return isObject(value) && isFunction(value.then);
    }
    const { isArray } = Array;
    function isFormData(value) {
        return value instanceof FormData;
    }
    function toArray(value) {
        return isArray(value) ? value : copyArrayLike(value);
    }
    function isList(value) {
        return isArray(value) ||
            isNodeList(value) ||
            isArguments(value) ||
            isJQuery(value) ||
            isHTMLCollection(value);
    }
    function isNodeList(value) {
        return value instanceof NodeList;
    }
    function isHTMLCollection(value) {
        return value instanceof HTMLCollection;
    }
    function isArguments(value) {
        return Object.prototype.toString.call(value) === '[object Arguments]';
    }
    function isAdjacentPosition(value) {
        return /^(before|after)/.test(value);
    }
    function wrapList(value) {
        if (isList(value)) {
            return value;
        }
        else if (isMissing(value)) {
            return [];
        }
        else {
            return [value];
        }
    }
    function copy(value) {
        if (isObject(value) && value[copy.key]) {
            value = value[copy.key]();
        }
        else if (isList(value)) {
            value = copyArrayLike(value);
        }
        else if (isOptions(value)) {
            value = Object.assign({}, value);
        }
        return value;
    }
    function copyArrayLike(arrayLike) {
        return Array.prototype.slice.call(arrayLike);
    }
    copy.key = 'up.util.copy';
    Date.prototype[copy.key] = function () { return new Date(+this); };
    function merge(...sources) {
        return Object.assign({}, ...sources);
    }
    function mergeDefined(...sources) {
        const result = {};
        for (let source of sources) {
            if (source) {
                for (let key in source) {
                    const value = source[key];
                    if (isDefined(value)) {
                        result[key] = value;
                    }
                }
            }
        }
        return result;
    }
    function newOptions(object, defaults) {
        if (defaults) {
            return merge(defaults, object);
        }
        else if (object) {
            return copy(object);
        }
        else {
            return {};
        }
    }
    function parseArgIntoOptions(args, argKey) {
        let [positionalArg, options] = parseArgs(args, 'val', 'options');
        if (isDefined(positionalArg)) {
            options[argKey] = positionalArg;
        }
        return options;
    }
    function findInList(list, tester) {
        tester = iteratee(tester);
        let match;
        for (let element of list) {
            if (tester(element)) {
                match = element;
                break;
            }
        }
        return match;
    }
    function some(list, tester) {
        return !!findResult(list, tester);
    }
    function findResult(list, tester) {
        tester = iteratee(tester);
        let i = 0;
        for (let item of list) {
            const result = tester(item, i++);
            if (result) {
                return result;
            }
        }
    }
    function every(list, tester) {
        tester = iteratee(tester);
        let match = true;
        let i = 0;
        for (let item of list) {
            if (!tester(item, i++)) {
                match = false;
                break;
            }
        }
        return match;
    }
    function compact(array) {
        return filterList(array, isGiven);
    }
    function compactObject(object) {
        return pickBy(object, isGiven);
    }
    function uniq(array) {
        if (array.length < 2) {
            return array;
        }
        return Array.from(new Set(array));
    }
    function uniqBy(array, mapper) {
        if (array.length < 2) {
            return array;
        }
        mapper = iteratee(mapper);
        const seenElements = new Set();
        return filterList(array, function (elem, index) {
            const mapped = mapper(elem, index);
            if (seenElements.has(mapped)) {
                return false;
            }
            else {
                seenElements.add(mapped);
                return true;
            }
        });
    }
    function filterList(list, tester) {
        tester = iteratee(tester);
        const matches = [];
        each(list, function (element, index) {
            if (tester(element, index)) {
                return matches.push(element);
            }
        });
        return matches;
    }
    function reject(list, tester) {
        tester = negate(iteratee(tester));
        return filterList(list, tester);
    }
    function intersect(array1, array2) {
        return filterList(array1, (element) => contains(array2, element));
    }
    function scheduleTimer(millis, callback) {
        return setTimeout(callback, millis);
    }
    function queueTask(task) {
        setTimeout(task);
    }
    function queueFastTask(task) {
        const channel = new MessageChannel();
        channel.port1.onmessage = () => task();
        channel.port2.postMessage(0);
    }
    function last(value) {
        return value[value.length - 1];
    }
    function contains(value, subValue) {
        let indexOf = value.indexOf || Array.prototype.indexOf;
        return indexOf.call(value, subValue) >= 0;
    }
    function containsAll(values, subValues) {
        return every(subValues, (subValue) => contains(values, subValue));
    }
    function objectContains(object, subObject) {
        const reducedValue = pick(object, Object.keys(subObject));
        return isEqual(subObject, reducedValue);
    }
    function pick(object, keys) {
        const filtered = {};
        for (let key of keys) {
            if (key in object) {
                filtered[key] = object[key];
            }
        }
        return filtered;
    }
    function pickBy(object, tester) {
        tester = iteratee(tester);
        const filtered = {};
        for (let key in object) {
            const value = object[key];
            if (tester(value, key)) {
                filtered[key] = object[key];
            }
        }
        return filtered;
    }
    function omit(object, keys) {
        return pickBy(object, (_value, key) => !contains(keys, key));
    }
    function unresolvablePromise() {
        return new Promise(noop);
    }
    function remove(array, element) {
        const index = array.indexOf(element);
        if (index >= 0) {
            array.splice(index, 1);
            return element;
        }
    }
    function evalOption(value, ...args) {
        return isFunction(value) ? value(...args) : value;
    }
    function evalAutoOption(value, autoMeans, ...args) {
        value = evalOption(value, ...args);
        if (value === 'auto') {
            value = evalOption(autoMeans, ...args);
        }
        return value;
    }
    const ESCAPE_HTML_ENTITY_MAP = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': '&quot;',
        "'": '&#x27;'
    };
    function escapeHTML(string) {
        return string.replace(/[&<>"']/g, (char) => ESCAPE_HTML_ENTITY_MAP[char]);
    }
    function escapeRegExp(string) {
        return string.replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
    }
    function pluckKey(object, key) {
        const value = object[key];
        delete object[key];
        return value;
    }
    function renameKey(object, oldKey, newKey) {
        if (oldKey in object) {
            object[newKey] = pluckKey(object, oldKey);
        }
    }
    function extractLastArg(args, tester) {
        if (tester(last(args))) {
            return args.pop();
        }
    }
    function extractCallback(args) {
        return extractLastArg(args, isFunction);
    }
    function extractOptions(args) {
        return extractLastArg(args, isOptions) || {};
    }
    function identity(arg) {
        return arg;
    }
    function sequence(...fns) {
        return (...callArgs) => callAll(fns, callArgs);
    }
    function callAll(fns, ...args) {
        fns = scanFunctions(fns);
        return map(fns, (fn) => fn(...args));
    }
    function flatten(array) {
        const flattened = [];
        for (let object of array) {
            if (isList(object)) {
                flattened.push(...object);
            }
            else {
                flattened.push(object);
            }
        }
        return flattened;
    }
    function flatMap(array, block) {
        return flatten(map(array, block));
    }
    function always(promise, callback = identity) {
        return promise.then(callback, callback);
    }
    function newDeferred() {
        let resolveFn;
        let rejectFn;
        const nativePromise = new Promise(function (givenResolve, givenReject) {
            resolveFn = givenResolve;
            rejectFn = givenReject;
        });
        nativePromise.resolve = resolveFn;
        nativePromise.reject = rejectFn;
        return nativePromise;
    }
    function isBasicObjectProperty(k) {
        return Object.prototype.hasOwnProperty(k);
    }
    function isEqual(a, b) {
        if (a === b) {
            return true;
        }
        if (a?.valueOf) {
            a = a.valueOf();
        }
        if (b?.valueOf) {
            b = b.valueOf();
        }
        if (a === b) {
            return true;
        }
        if (typeof (a) !== typeof (b)) {
            return false;
        }
        if (isList(a) && isList(b)) {
            return isEqualList(a, b);
        }
        if (isObject(a) && a[isEqual.key]) {
            return a[isEqual.key](b);
        }
        if (isOptions(a) && isOptions(b)) {
            return isEqualOptions(a, b);
        }
        return false;
    }
    function isEqualOptions(a, b) {
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        return isEqualList(aKeys, bKeys) && every(aKeys, (key) => isEqual(a[key], b[key]));
    }
    isEqual.key = 'up.util.isEqual';
    function isEqualList(a, b) {
        return (a.length === b.length) && every(a, (elem, index) => isEqual(elem, b[index]));
    }
    function getSimpleTokens(value, { json = false, separator = /[,\s]/ } = {}) {
        if (!isString(value)) {
            return wrapList(value);
        }
        else if (json && /^\[.*]$/.test(value)) {
            return parseRelaxedJSON(value);
        }
        else {
            return splitSimpleTokenString(value, separator);
        }
    }
    function splitSimpleTokenString(value, separator) {
        let parts = up.migrate.splitAtOr?.(value) || value.split(separator);
        return parts.map((s) => s.trim()).filter(identity);
    }
    function getComplexTokens(value) {
        if (!isString(value)) {
            return wrapList(value);
        }
        else {
            let { maskedTokens, restore } = complexTokenOutlines(value);
            return maskedTokens.map((token) => restore(token));
        }
    }
    function complexTokenOutlines(string) {
        let { masked, restore } = expressionOutline(string);
        let maskedTokens = splitSimpleTokenString(masked, ',');
        return { maskedTokens, restore };
    }
    function wrapValue(constructor, ...args) {
        return (args[0] instanceof constructor) ? args[0] : new constructor(...args);
    }
    let nextUid = 0;
    function uid() {
        return nextUid++;
    }
    function withRenamedKeys(object, renameKeyFn) {
        const renamed = {};
        for (let key in object) {
            let transformed = renameKeyFn(key);
            if (isGiven(transformed)) {
                renamed[transformed] = object[key];
            }
        }
        return renamed;
    }
    function camelToKebabCase(str) {
        return str.replace(/[A-Z]/g, (char) => '-' + char.toLowerCase());
    }
    function kebabToCamelCase(str) {
        return str.replace(/-([a-z])/g, (_match, char) => char.toUpperCase());
    }
    function lowerCaseFirst(str) {
        return str[0].toLowerCase() + str.slice(1);
    }
    function upperCaseFirst(str) {
        return str[0].toUpperCase() + str.slice(1);
    }
    function defineDelegates(object, props, targetProvider) {
        for (let prop of props) {
            Object.defineProperty(object, prop, {
                get() {
                    const target = targetProvider.call(this);
                    let value = target[prop];
                    if (isFunction(value)) {
                        value = value.bind(target);
                    }
                    return value;
                },
                set(newValue) {
                    const target = targetProvider.call(this);
                    target[prop] = newValue;
                }
            });
        }
    }
    function delegatePromise(object, targetProvider) {
        return defineDelegates(object, ['then', 'catch', 'finally'], targetProvider);
    }
    function stringifyArg(arg, placeholder = '%o') {
        let string;
        const maxLength = 200;
        if (placeholder === '%c') {
            return '';
        }
        if (placeholder === '%s' && isGiven(arg)) {
            arg = arg.toString();
        }
        if (isString(arg)) {
            string = arg.trim().replace(/[\n\r\t ]+/g, ' ');
            if (placeholder === '%o') {
                string = JSON.stringify(string);
            }
        }
        else if (isUndefined(arg)) {
            string = 'undefined';
        }
        else if (isNumber(arg) || isFunction(arg)) {
            string = arg.toString();
        }
        else if (isArray(arg)) {
            string = `[${map(arg, stringifyArg).join(', ')}]`;
        }
        else if (isJQuery(arg)) {
            string = `$(${map(arg, stringifyArg).join(', ')})`;
        }
        else if (isElement(arg)) {
            string = tagOuterHTML(arg);
        }
        else if (isRegExp(arg) || isError(arg)) {
            string = arg.toString();
        }
        else {
            try {
                string = JSON.stringify(arg);
            }
            catch (error) {
                if (error.name === 'TypeError') {
                    string = '(circular structure)';
                }
                else {
                    throw error;
                }
            }
        }
        if (string.length > maxLength) {
            string = `${string.substr(0, maxLength)}…${last(string)}`;
        }
        return string;
    }
    const SPRINTF_PLACEHOLDERS = /%[oOdisfc]/g;
    function sprintf(message, ...args) {
        return message.replace(SPRINTF_PLACEHOLDERS, (placeholder) => stringifyArg(args.shift(), placeholder));
    }
    const SERIALIZED_TAG_PATTERN = /<("[^"]*"|[^">])*>/;
    function tagOuterHTML(element) {
        return element.cloneNode().outerHTML.match(SERIALIZED_TAG_PATTERN)[0];
    }
    function negate(fn) {
        return function (...args) {
            return !fn(...args);
        };
    }
    function memoizeMethod(object, propLiteral) {
        for (let prop in propLiteral) {
            let originalDescriptor = Object.getOwnPropertyDescriptor(object, prop);
            let oldImpl = originalDescriptor.value;
            let memoizedImpl = memoize(oldImpl, `__${prop}MemoizeCache`);
            object[prop] = memoizedImpl;
        }
    }
    function safeStringifyJSON(value) {
        let json = JSON.stringify(value);
        return escapeHighASCII(json);
    }
    function escapeHighASCII(string) {
        let unicodeEscape = (char) => "\\u" + char.charCodeAt(0).toString(16).padStart(4, '0');
        return string.replace(/[^\x00-\x7F]/g, unicodeEscape);
    }
    function variant(source, changes = {}) {
        let variant = Object.create(source);
        Object.assign(variant, changes);
        return variant;
    }
    function parseArgs(args, ...specs) {
        let results = [];
        while (specs.length) {
            let lastSpec = specs.pop();
            if (lastSpec === 'options') {
                results.unshift(extractOptions(args));
            }
            else if (lastSpec === 'callback') {
                results.unshift(extractCallback(args));
            }
            else if (lastSpec === 'val') {
                results.unshift(args.pop());
            }
            else if (isFunction(lastSpec)) {
                let value = lastSpec(last(args)) ? args.pop() : undefined;
                results.unshift(value);
            }
        }
        return results;
    }
    function scanFunctions(...values) {
        return values.flat(2).filter(isFunction);
    }
    function cleaner(order = 'lifo') {
        let fns = [];
        let track = function (values, transform) {
            values = scanFunctions(values).map(transform);
            fns.push(...scanFunctions(values));
        };
        let api = function (...values) {
            track(values, identity);
        };
        api.guard = function (...values) {
            track(values, up.error.guardFn);
        };
        api.clean = function (...args) {
            if (order === 'lifo')
                fns.reverse();
            fns.forEach((fn) => fn(...args));
            fns = [];
        };
        return api;
    }
    function maskPattern(str, patterns, { keepDelimiters = false } = {}) {
        let maskCount = 0;
        let maskPattern = /§\d+/g;
        let maskings = {};
        let replaceLayers = [];
        let replace = (replacePattern, allowRestoreTransform) => {
            let didReplace = false;
            str = str.replaceAll(replacePattern, function (match) {
                didReplace = true;
                let mask = '§' + (maskCount++);
                let remain;
                let masked;
                if (keepDelimiters) {
                    let startDelimiter = match[0];
                    let endDelimiter = match.slice(-1);
                    masked = match.slice(1, -1);
                    remain = startDelimiter + mask + endDelimiter;
                }
                else {
                    masked = match;
                    remain = mask;
                }
                maskings[mask] = masked;
                return remain;
            });
            if (didReplace)
                replaceLayers.unshift({ allowRestoreTransform });
        };
        replace(maskPattern, false);
        for (let pattern of patterns)
            replace(pattern, true);
        let restore = (s, transform = identity) => {
            for (let { allowRestoreTransform } of replaceLayers) {
                let iterationTransform = allowRestoreTransform ? transform : identity;
                s = s.replace(maskPattern, (match) => iterationTransform(assert(maskings[match], isString)));
            }
            return s;
        };
        return { masked: str, restore };
    }
    const QUOTED_STRING_PATTERN = /'(?:\\\\|\\'|[^'])*'|"(?:\\\\|\\"|[^"])*"/g;
    const NESTED_GROUP_PATTERN = /{(?:[^{}]|{[^{}]*})*}|\((?:[^\(\)]|\([^\(\)]*\))*\)|\[(?:[^\[\]]|\[[^\[\]]*\])*\]/g;
    function expressionOutline(str) {
        return maskPattern(str, [QUOTED_STRING_PATTERN, NESTED_GROUP_PATTERN], { keepDelimiters: true });
    }
    function ensureDoubleQuotes(str) {
        if (str[0] === '"')
            return str;
        assert(str[0] === "'");
        str = str.slice(1, -1);
        let transformed = str.replace(/(\\\\)|(\\')|(\\")|(")/g, function (_match, escapedBackslash, escapedSingleQuote, _doubleQuote) {
            return escapedBackslash
                || (escapedSingleQuote && "'")
                || '\\"';
        });
        return '"' + transformed + '"';
    }
    function parseString(value) {
        return JSON.parse(ensureDoubleQuotes(value));
    }
    function parseRelaxedJSON(str) {
        let { masked, restore } = maskPattern(str, [QUOTED_STRING_PATTERN]);
        masked = masked.replace(/([a-z_$][\w$]*:)/gi, (unquotedProperty) => ('"' + unquotedProperty.slice(0, -1) + '":'));
        masked = masked.replace(/,\s*([}\]])/g, '$1');
        masked = restore(masked, ensureDoubleQuotes);
        return JSON.parse(masked);
    }
    function parseScalarJSONPairs(str) {
        let { maskedTokens, restore } = complexTokenOutlines(str);
        return maskedTokens.map((maskedToken) => {
            let [_match, string, json] = maskedToken.match(/([^{]+)({[^}]*})?/);
            return [
                restore(string.trim()),
                json && parseRelaxedJSON(restore(json))
            ];
        });
    }
    function spanObject(keys, value) {
        return mapObject(keys, (key) => [key, value]);
    }
    function parseNumber(str) {
        if (isNumber(str)) {
            return str;
        }
        else if (isString(str) && /^-?\.?\d[\d._]*$/.test(str)) {
            return parseFloat(str.replaceAll('_', ''));
        }
    }
    function assert(value, testFn = isTruthy) {
        if (testFn(value)) {
            return value;
        }
        else {
            throw "assert failed";
        }
    }
    return {
        parseURL,
        normalizeURL,
        matchableURL,
        matchableURLPatternAtom,
        matchURLs,
        normalizeMethod,
        methodAllowsPayload,
        copy,
        merge,
        mergeDefined,
        options: newOptions,
        parseArgIntoOptions,
        each,
        map,
        flatMap,
        mapObject,
        spanObject,
        findResult,
        some,
        every,
        find: findInList,
        filter: filterList,
        reject,
        intersect,
        compact,
        compactObject,
        uniq,
        uniqBy,
        last,
        isNull,
        isDefined,
        isUndefined,
        isGiven,
        isMissing,
        isPresent,
        isBlank,
        presence,
        isObject,
        isFunction,
        isString,
        isBoolean,
        isNumber,
        isElement,
        isTextNode,
        isJQuery,
        isElementLike,
        isPromise,
        isOptions,
        isArray,
        isFormData,
        isList,
        isRegExp,
        isAdjacentPosition,
        timer: scheduleTimer,
        contains,
        containsAll,
        objectContains,
        toArray,
        pick,
        pickBy,
        omit,
        unresolvablePromise,
        remove,
        memoize,
        pluckKey,
        renameKey,
        extractOptions,
        extractCallback,
        noop,
        asyncNoop,
        identity,
        escapeHTML,
        escapeRegExp,
        sequence,
        callAll,
        evalOption,
        evalAutoOption,
        flatten,
        newDeferred,
        always,
        isBasicObjectProperty,
        isCrossOrigin,
        task: queueTask,
        fastTask: queueFastTask,
        isEqual,
        getSimpleTokens,
        getComplexTokens,
        wrapList,
        wrapValue,
        uid,
        upperCaseFirst,
        lowerCaseFirst,
        delegate: defineDelegates,
        delegatePromise,
        camelToKebabCase,
        kebabToCamelCase,
        sprintf,
        withRenamedKeys,
        memoizeMethod,
        safeStringifyJSON,
        variant,
        cleaner,
        scanFunctions,
        args: parseArgs,
        parseRelaxedJSON,
        parseScalarJSONPairs,
        maskPattern,
        expressionOutline,
        parseString,
        assert,
        parseNumber,
    };
})();
